import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import PendingImageCropEditor from '~/components/Common/Images/PendingImageCropEditor';
import {
  createCroppedImageFile,
  getCenteredCropArea,
  getCropAreaForAspectRatio,
  getDefaultPendingCropAspectRatio,
  getImageDimensions,
  normalizeImageFileForUpload,
} from '~/components/Common/Images/cropUtils';
import { DEFAULT_PENDING_IMAGE_ADJUSTMENTS, PendingImageItem } from '~/components/Common/Images/pendingUploadTypes';

import { MAX_SVC_IMAGE_UPLOAD_BYTES } from '~/services/ImageService';

import getErrorMessage from '~/utils/getErrorMessage';

interface Props {
  file?: File;
  onCancel: () => void;
  onSave: (file: File) => Promise<boolean>;
}

export default function OversizedImageEditor({ file, onCancel, onSave }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [item, setItem] = useState<PendingImageItem>();
  const [imageError, setImageError] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let objectUrl: string | undefined;
    let cancelled = false;

    if (!file) {
      setItem(undefined);
      setImageError(undefined);
      return undefined;
    }

    setItem(undefined);
    setImageError(undefined);

    void getImageDimensions(file)
      .then((dimensions) => {
        if (cancelled) {
          return;
        }
        objectUrl = URL.createObjectURL(file);
        const fixedAspectRatio = getDefaultPendingCropAspectRatio(dimensions, 16 / 9);
        setItem({
          localId: file.name,
          file,
          filename: file.name,
          objectUrl,
          description: '',
          zoom: 1,
          cropMode: 'fixed',
          fixedAspectRatio,
          croppedAreaPixels: getCenteredCropArea(dimensions, fixedAspectRatio),
          dimensions,
          adjustments: { ...DEFAULT_PENDING_IMAGE_ADJUSTMENTS },
          status: 'draft',
        });
      })
      .catch((error) => {
        if (!cancelled) {
          setImageError(getErrorMessage(error, 'Unable to load image for editing'));
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [file]);

  const handleSave = async () => {
    if (!item) {
      return;
    }
    setIsSaving(true);
    try {
      const croppedFile = await createCroppedImageFile({
        adjustments: item.adjustments,
        croppedAreaPixels: item.croppedAreaPixels,
        file: item.file,
        outputFileName: item.filename,
      });
      let uploadFile: File;
      try {
        uploadFile = await normalizeImageFileForUpload({
          file: croppedFile,
          maxBytes: MAX_SVC_IMAGE_UPLOAD_BYTES,
          outputFileName: item.filename,
        });
      } catch {
        enqueueSnackbar('This image is still too large. Crop it further or choose a smaller image.', {
          variant: 'error',
        });
        return;
      }
      const uploaded = await onSave(uploadFile);

      if (uploaded) {
        onCancel();
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'Unable to prepare image for upload'), { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" open={!!file} onClose={isSaving ? undefined : onCancel}>
      <DialogTitle>Edit image before uploading</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography color="text.secondary" variant="body2">
            This image is too large to upload directly. Crop it before uploading.
          </Typography>
          {imageError ? (
            <Alert severity="error">{imageError}</Alert>
          ) : item ? (
            <>
              <Stack alignItems="center" direction="row" justifyContent="space-between">
                <Typography variant="subtitle2">Crop mode</Typography>
                <ToggleButtonGroup
                  exclusive
                  disabled={isSaving}
                  size="small"
                  value={item.cropMode}
                  onChange={(_, cropMode: PendingImageItem['cropMode'] | null) => {
                    if (!cropMode) {
                      return;
                    }

                    setItem((currentItem) =>
                      currentItem
                        ? {
                            ...currentItem,
                            cropMode,
                            croppedAreaPixels:
                              cropMode === 'fixed'
                                ? getCropAreaForAspectRatio({
                                    aspectRatio: currentItem.fixedAspectRatio,
                                    cropArea: currentItem.croppedAreaPixels,
                                    dimensions: currentItem.dimensions,
                                  })
                                : currentItem.croppedAreaPixels,
                          }
                        : currentItem,
                    );
                  }}
                >
                  <ToggleButton value="fixed">16:9</ToggleButton>
                  <ToggleButton value="freeform">Freeform</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <PendingImageCropEditor
                disabled={isSaving}
                item={item}
                onCropChange={(_localId, croppedAreaPixels, zoom) =>
                  setItem((currentItem) => (currentItem ? { ...currentItem, croppedAreaPixels, zoom } : currentItem))
                }
              />
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={isSaving} variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={!item || isSaving} variant="contained" onClick={handleSave}>
          {isSaving ? 'Preparing...' : 'Crop and upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
